/**
 * Replace template variables in a string with actual values
 * @param {string} text - Text containing variables like $account_id or ${account_id}
 * @param {object} variables - Object mapping variable names to values
 * @returns {string} Text with variables replaced
 */
export const replaceVariables = (text, variables) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  let result = text;

  // Replace each variable
  Object.keys(variables).forEach(varName => {
    const value = variables[varName];
    
    // Replace $varName format
    const dollarRegex = new RegExp(`\\$${varName}\\b`, 'g');
    result = result.replace(dollarRegex, value);
    
    // Replace ${varName} format
    const braceRegex = new RegExp(`\\$\\{${varName}\\}`, 'g');
    result = result.replace(braceRegex, value);
  });

  return result;
};

/**
 * Replace variables in a panel's targets/queries
 * @param {object} panel - Panel object with targets
 * @param {object} variables - Variable values
 * @returns {object} Panel with replaced variables
 */
export const replacePanelVariables = (panel, variables) => {
  if (!panel.targets || panel.targets.length === 0) {
    return panel;
  }

  const updatedTargets = panel.targets.map(target => ({
    ...target,
    expr: replaceVariables(target.expr, variables)
  }));

  return {
    ...panel,
    targets: updatedTargets
  };
};

/**
 * Extract variable names from text
 * @param {string} text - Text to search for variables
 * @returns {array} Array of variable names found
 */
export const extractVariableNames = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const variables = new Set();
  
  // Match $varName
  const dollarMatches = text.match(/\$([a-zA-Z_][a-zA-Z0-9_]*)/g);
  if (dollarMatches) {
    dollarMatches.forEach(match => {
      variables.add(match.substring(1)); // Remove $
    });
  }
  
  // Match ${varName}
  const braceMatches = text.match(/\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g);
  if (braceMatches) {
    braceMatches.forEach(match => {
      const varName = match.substring(2, match.length - 1); // Remove ${ and }
      variables.add(varName);
    });
  }

  return Array.from(variables);
};